import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import { FolderShared as FolderSharedIcon } from '@mui/icons-material';
import cfosService from '../services/cfosService';
import CfosWorkspace from '../components/cfos/CfosWorkspace';
import type { CfosSandbox, User } from '../lib/cfos/types';

type SandboxFilter = 'shared' | 'all' | string;

const CfosPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [user, setUser] = useState<User | null>(null);
  const [sandboxes, setSandboxes] = useState<CfosSandbox[]>([]);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState<SandboxFilter>('shared');
  const [activeSandboxId, setActiveSandboxId] = useState<string | null>(null);

  const panelBorder = isDark ? 'rgba(204, 186, 155, 0.15)' : 'rgba(15, 33, 52, 0.12)';
  const sand = '#ccba9b';

  useEffect(() => {
    Promise.all([cfosService.me(), cfosService.listSandboxes()])
      .then(([me, list]) => {
        setUser(me);
        setSandboxes(list);
        const shared = list.find((item) => item.is_shared);
        if (shared) {
          setActiveSandboxId(shared.id);
          setFilter('shared');
        } else if (list[0]) {
          setActiveSandboxId(list[0].id);
          setFilter(list[0].id);
        }
      })
      .catch((error: Error) => setLoadError(error.message));
  }, []);

  const projectSandboxes = useMemo(
    () => sandboxes.filter((item) => !item.is_shared).sort((a, b) => a.name.localeCompare(b.name)),
    [sandboxes],
  );
  const sharedSandbox = useMemo(() => sandboxes.find((item) => item.is_shared) || null, [sandboxes]);

  const onFilterChange = (value: SandboxFilter) => {
    setFilter(value);
    if (value === 'shared' && sharedSandbox) {
      setActiveSandboxId(sharedSandbox.id);
      return;
    }
    if (value === 'all') {
      // Browse/Upload still need a concrete sandbox; default to Shared when available.
      setActiveSandboxId(sharedSandbox?.id || projectSandboxes[0]?.id || null);
      return;
    }
    setActiveSandboxId(value);
  };

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  if (!user || !activeSandboxId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress sx={{ color: sand }} />
      </Box>
    );
  }

  const searchSandboxId = filter === 'all' ? null : activeSandboxId;
  const activeLabel =
    filter === 'all'
      ? 'All sandboxes'
      : sandboxes.find((item) => item.id === activeSandboxId)?.name || 'CFOS';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 40,
            height: 40,
            borderRadius: 1,
            border: 1,
            borderColor: panelBorder,
            color: sand,
          }}
        >
          <FolderSharedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h5" fontWeight={700} color={isDark ? '#fff' : '#0f2134'}>
            CFOS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Collaborative file organization · {user.name} {user.last_name} · {activeLabel}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="cfos-sandbox-filter-label">Sandbox</InputLabel>
          <Select
            labelId="cfos-sandbox-filter-label"
            label="Sandbox"
            value={filter}
            onChange={(event) => onFilterChange(event.target.value as SandboxFilter)}
          >
            <MenuItem value="shared">Shared</MenuItem>
            <MenuItem value="all">All sandboxes</MenuItem>
            {projectSandboxes.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filter === 'all' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Search spans all sandboxes. Browse and Upload use {sharedSandbox ? 'Shared' : 'the selected'} sandbox —
          pick a project sandbox to upload into a project tree.
        </Alert>
      )}

      <CfosWorkspace
        user={user}
        sandboxId={activeSandboxId}
        allowLegacyFolderImport={Boolean(sharedSandbox && activeSandboxId === sharedSandbox.id)}
        searchSandboxId={searchSandboxId}
      />
    </Box>
  );
};

export default CfosPage;
